import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import { UNITS, formatAED } from "../../data/project";

export default function Investment() {
  return (
    <section
      id="investment"
      data-testid="investment-section"
      className="relative bg-[#F4F2EC] py-24 md:py-36"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-5">03 / Investment</div>
            <h2
              className="font-serif leading-[1.02] tracking-tight text-[#1A3626]"
              style={{ fontSize: "clamp(40px, 5.5vw, 78px)" }}
            >
              A four-year hold.
              <br />
              <span className="italic font-light">A capital case.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-[#1A3626]/75 font-light leading-relaxed">
              Modon's payment structure releases just 40% of capital prior to
              handover in March 2030, concentrating equity gains in the years
              when Hudayriyat completes. Below, the case for each typology,
              based on a conservative 50% appreciation modelled on comparable
              Abu Dhabi launches.
            </p>
          </div>
        </div>

        {/* Headline KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#B3906A]/30 border-y hairline">
          <Kpi label="Capital Appreciation Forecast" value="+50%" sub="Over 4-yr construction" />
          <Kpi label="Return on Equity (range)" value="114 – 118%" sub="Across typologies" />
          <Kpi label="Equity Released Pre-Handover" value="40%" sub="Per Modon schedule" />
          <Kpi label="Handover" value="Mar 2030" sub="60% payable on completion" />
        </div>

        {/* Detailed table */}
        <div className="mt-20">
          <div className="overline mb-6">Per-Typology Investment Case</div>
          <div className="overflow-x-auto hge-scroll-x border hairline">
            <table className="w-full min-w-[860px] text-left">
              <thead>
                <tr className="text-[10px] tracking-[0.22em] uppercase text-[#1A3626]/65 border-b hairline bg-[#F4F2EC]">
                  <th className="py-4 px-5 font-medium">Typology</th>
                  <th className="py-4 px-5 font-medium">Sqm</th>
                  <th className="py-4 px-5 font-medium text-right">Price</th>
                  <th className="py-4 px-5 font-medium text-right">Equity in (pre-HO)</th>
                  <th className="py-4 px-5 font-medium text-right">Est. Value at HO</th>
                  <th className="py-4 px-5 font-medium text-right">Capital Gain</th>
                  <th className="py-4 px-5 font-medium text-right">ROE</th>
                </tr>
              </thead>
              <tbody>
                {UNITS.map((u, i) => (
                  <motion.tr
                    key={u.id}
                    data-testid={`investment-row-${u.id}`}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="border-b hairline last:border-0 hover:bg-[#1A3626]/[0.03] transition-colors"
                  >
                    <td className="py-5 px-5">
                      <div className="font-serif text-lg md:text-xl text-[#1A3626]">
                        {u.name}
                      </div>
                    </td>
                    <td className="py-5 px-5 text-[#6A736E] text-sm">
                      {u.sqm}
                    </td>
                    <td className="py-5 px-5 text-right font-serif text-lg text-[#1A3626]">
                      {formatAED(u.priceAED)}
                    </td>
                    <td className="py-5 px-5 text-right text-[#6A736E] text-sm tabular-nums">
                      {formatAED(u.equityBeforeHandover)}
                    </td>
                    <td className="py-5 px-5 text-right font-serif text-lg text-[#1A3626] tabular-nums">
                      {formatAED(u.estValueHandover)}
                    </td>
                    <td className="py-5 px-5 text-right text-[#B3906A] font-serif text-lg tabular-nums">
                      +{formatAED(u.capitalGain)}
                    </td>
                    <td className="py-5 px-5 text-right">
                      <span className="inline-flex items-center gap-1.5 font-serif text-xl text-[#1A3626]">
                        <TrendingUp className="w-4 h-4 text-[#B3906A]" strokeWidth={1.5} />
                        {u.roiOnEquity}%
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 text-xs text-[#6A736E] font-light max-w-3xl leading-relaxed">
            Capital appreciation modelled at 50% over the four-year construction
            window, benchmarked against historical performance of comparable
            island-led Abu Dhabi launches (Saadiyat, Yas, Reem). Rental yield
            on Hudayriyat is forecast at 6–8% post-handover. Figures
            indicative; full investment memo available on request.
          </p>
        </div>

        {/* Yield narrative */}
        <div className="mt-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="overline mb-4">Why Hudayriyat</div>
          </div>
          <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-y-10 gap-x-8">
            <Thesis
              num="i."
              title="Scarcity by design"
              body="One island. One golf course. A finite number of fairway-fronting and water-fronting lots that cannot be replicated within Abu Dhabi's urban core."
            />
            <Thesis
              num="ii."
              title="Master-developer covenant"
              body="Modon is the master developer of Hudayriyat, controlling roads, retail, hospitality and schools. Value is captured by residents, not eroded by adjacent developers."
            />
            <Thesis
              num="iii."
              title="20-minute capital access"
              body="Connected by causeway to the Corniche, ADGM and Saadiyat. Closer to downtown than most Yas or Khalifa City addresses."
            />
            <Thesis
              num="iv."
              title="40 / 60 capital efficiency"
              body="Only 40% of price is staged through construction; gains compound on equity, not the full purchase price."
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="bg-[#F4F2EC] p-6 md:p-9"
    >
      <div className="overline">{label}</div>
      <div
        className="mt-3 font-serif text-[#1A3626] leading-[1] tracking-tight"
        style={{ fontSize: "clamp(32px, 3.6vw, 56px)" }}
      >
        {value}
      </div>
      <div className="mt-2 text-xs text-[#6A736E] font-light">{sub}</div>
    </div>
  );
}

function Thesis({ num, title, body }) {
  return (
    <div className="border-t hairline pt-6">
      <div className="text-[#B3906A] font-serif text-lg italic">{num}</div>
      <h4 className="mt-2 font-serif text-2xl md:text-3xl text-[#1A3626] leading-tight">
        {title}
      </h4>
      <p className="mt-3 text-[#6A736E] font-light leading-relaxed">{body}</p>
    </div>
  );
}
